import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import gmfLogo from "@/assets/gmf-logo.png";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  prefetchCustomFurnitureRoutes,
  prefetchBlindsCurtainsRoutes,
  prefetchUpholsteryRoutes,
  prefetchProjectsRoute,
  prefetchContactRoute,
} from "@/lib/routePrefetch";

const customFurnitureItems = [
  { name: "All Custom Furniture", href: "/customize-furniture" },
  { name: "Custom Bed & Headboard Dubai", href: "/customize-furniture/bed-headboard" },
  { name: "Custom Sofa & Chair Dubai", href: "/customize-furniture/sofa-chair" },
  { name: "Wall & TV Unit Dubai", href: "/customize-furniture/wall-tv-unit" },
  { name: "Custom Wardrobe & Cabinet Dubai", href: "/customize-furniture/wardrobe-cabinet" },
];

const blindItems = [
  { name: "All Blinds", href: "/blinds" },
  { name: "Roller Blind Dubai", href: "/blinds-curtains/blinds/roller" },
  { name: "Duplex Blind Dubai", href: "/blinds-curtains/blinds/duplex" },
  { name: "Sheer Horizontal Blinds", href: "/blinds-curtains/blinds/sheer-horizontal" },
  { name: "Bamboo Blinds Dubai", href: "/blinds-curtains/blinds/bamboo" },
  { name: "Vertical Blinds Dubai", href: "/blinds-curtains/blinds/vertical" },
  { name: "Aluminium Venetian Blind", href: "/blinds-curtains/blinds/aluminium-venetian" },
  { name: "Panel Blind Dubai", href: "/blinds-curtains/blinds/panel" },
  { name: "Wooden Blind Dubai", href: "/blinds-curtains/blinds/wooden" },
  { name: "Roman Blind Dubai", href: "/blinds-curtains/blinds/roman" },
];

const curtainItems = [
  { name: "All Curtains", href: "/curtains" },
  { name: "Rings Curtains Dubai", href: "/blinds-curtains/curtains/rings" },
  { name: "Eyelet Curtains Dubai", href: "/blinds-curtains/curtains/eyelet" },
  { name: "Pinch Pleat Curtains Dubai", href: "/blinds-curtains/curtains/pinch-pleat" },
  { name: "Ripplefold Curtains Dubai", href: "/blinds-curtains/curtains/ripplefold" },
  { name: "Loops Curtains Dubai", href: "/blinds-curtains/curtains/loops" },
  { name: "Blackout Curtains Dubai", href: "/blinds-curtains/curtains/blackout" },
  { name: "Sheer Curtains Dubai", href: "/blinds-curtains/curtains/sheers" },
  { name: "Linen Curtains Dubai", href: "/blinds-curtains/curtains/linen" },
  { name: "Velvet Curtains Dubai", href: "/blinds-curtains/curtains/velvet" },
  { name: "Kids Room Curtains", href: "/blinds-curtains/curtains/kids-room" },
  { name: "Bedroom Curtains Dubai", href: "/blinds-curtains/curtains/bedroom" },
  { name: "Living Room Curtains", href: "/blinds-curtains/curtains/living-room" },
  { name: "Curtain Fixing & Installation", href: "/blinds-curtains/curtains/installation" },
];

const indoorUpholsteryItems = [
  { name: "All Indoor Upholstery", href: "/upholstery/indoor" },
  { name: "Sofa Set Upholstery Dubai", href: "/upholstery/indoor/sofa-set" },
  { name: "Bed Upholstery Dubai", href: "/upholstery/indoor/bed" },
  { name: "Chairs Upholstery Dubai", href: "/upholstery/indoor/chairs" },
  { name: "Cushions Upholstery", href: "/upholstery/indoor/cushions" },
  { name: "Headboard Upholstery Dubai", href: "/upholstery/indoor/headboard" },
  { name: "Wall Panels Upholstery", href: "/upholstery/indoor/wall-panels" },
  { name: "Bedbase Upholstery Dubai", href: "/upholstery/indoor/bedbase" },
  { name: "Sponge Repairing & Filling", href: "/upholstery/indoor/sponge-repairing" },
];

const outdoorUpholsteryItems = [
  { name: "All Outdoor Upholstery", href: "/upholstery/outdoor" },
  { name: "Outdoor Cushions Dubai", href: "/upholstery/outdoor/cushions" },
  { name: "Outdoor Sunbed Cushions", href: "/upholstery/outdoor/sunbed-cushions" },
  { name: "Outdoor Sofa Cushions Dubai", href: "/upholstery/outdoor/sofa-cushions" },
  { name: "Outdoor Furniture Upholstery", href: "/upholstery/outdoor/furniture" },
  { name: "Outdoor Cushions Manufacturing", href: "/upholstery/outdoor/manufacturing" },
  { name: "Custom Outdoor Protection Cover", href: "/upholstery/outdoor/covers" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const { t, isRTL, language } = useLanguage();

  // Improved active state checking
  const isHomeActive = location.pathname === "/";
  const isCustomFurnitureActive = location.pathname.startsWith("/customize-furniture");
  const isBlindsCurtainsActive = location.pathname.startsWith("/blinds-curtains") || 
                                  location.pathname.startsWith("/blinds") || 
                                  location.pathname.startsWith("/curtains");
  const isUpholsteryActive = location.pathname.startsWith("/upholstery");
  const isProjectsActive = location.pathname.startsWith("/projects");
  const isContactActive = location.pathname.startsWith("/contact");

  // Check if specific submenu item is active
  const isItemActive = (href: string) => location.pathname === href;

  // Active link styles
  const activeLinkClass = "text-gold font-semibold";
  const activeDropdownItemClass = "bg-gold/10 text-gold font-medium";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-gradient-to-r from-background via-background to-accent/10 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      <nav className="container mx-auto flex h-20 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-3 transition-transform duration-300 hover:scale-105">
          <img src={gmfLogo} alt="GMF Custom Furniture Dubai" className="h-16 w-auto" width={64} height={64} fetchPriority="high" decoding="sync" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-sm font-semibold text-foreground">Ghulam Mustafa Furniture</span>
            <span className="text-xs text-gold font-medium" style={{ fontFamily: 'Arial, sans-serif' }}>غلام مصطفى لتجارة الاثاث ش.ذ.م.م</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:gap-1">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <Link 
                  to="/" 
                  className={`px-4 py-2 text-sm font-medium transition-colors hover:text-gold ${isHomeActive ? activeLinkClass : "text-foreground"}`}
                >
                  Home
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={isCustomFurnitureActive ? activeLinkClass : ""}
                  onMouseEnter={prefetchCustomFurnitureRoutes}
                  onTouchStart={prefetchCustomFurnitureRoutes}
                >
                  Custom Furniture Dubai
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-1 p-4">
                    {customFurnitureItems.map((item) => (
                      <li key={item.name}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={item.href}
                            className={`block select-none rounded-md p-3 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-gold focus:bg-accent ${isItemActive(item.href) ? activeDropdownItemClass : ""}`}
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={isBlindsCurtainsActive ? activeLinkClass : ""}
                  onMouseEnter={prefetchBlindsCurtainsRoutes}
                  onTouchStart={prefetchBlindsCurtainsRoutes}
                >
                  Blinds & Curtains Dubai
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[700px] grid-cols-2 gap-4 p-4">
                    <div>
                      <Link 
                        to="/blinds-curtains" 
                        className={`mb-2 font-semibold hover:underline block ${isItemActive("/blinds-curtains") ? "text-gold" : "text-gold"}`}
                      >
                        All Blinds & Curtains
                      </Link>
                      <h3 className="mb-2 font-semibold text-gold">Blinds Dubai</h3>
                      <ul className="space-y-1">
                        {blindItems.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className={`block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-gold focus:bg-accent ${isItemActive(item.href) ? activeDropdownItemClass : ""}`}
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="mb-2 font-semibold text-gold">Curtains Dubai</h3>
                      <ul className="space-y-1 max-h-[400px] overflow-y-auto">
                        {curtainItems.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className={`block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-gold focus:bg-accent ${isItemActive(item.href) ? activeDropdownItemClass : ""}`}
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className={isUpholsteryActive ? activeLinkClass : ""}
                  onMouseEnter={prefetchUpholsteryRoutes}
                  onTouchStart={prefetchUpholsteryRoutes}
                >
                  Upholstery Dubai
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[600px] grid-cols-2 gap-4 p-4">
                    <div>
                      <Link 
                        to="/upholstery" 
                        className={`mb-2 font-semibold hover:underline block ${isItemActive("/upholstery") ? "text-gold" : "text-gold"}`}
                      >
                        All Upholstery Services
                      </Link>
                      <h3 className="mb-2 font-semibold text-gold">Indoor Upholstery</h3>
                      <ul className="space-y-1">
                        {indoorUpholsteryItems.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className={`block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-gold focus:bg-accent ${isItemActive(item.href) ? activeDropdownItemClass : ""}`}
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="mb-2 font-semibold text-gold">Outdoor Upholstery</h3>
                      <ul className="space-y-1">
                        {outdoorUpholsteryItems.map((item) => (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className={`block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-gold focus:bg-accent ${isItemActive(item.href) ? activeDropdownItemClass : ""}`}
                              >
                                {item.name}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link 
                  to="/projects" 
                  className={`px-4 py-2 text-sm font-medium transition-colors hover:text-gold ${isProjectsActive ? activeLinkClass : "text-foreground"}`}
                  onMouseEnter={prefetchProjectsRoute}
                  onTouchStart={prefetchProjectsRoute}
                >
                  Projects
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link 
                  to="/contact" 
                  className={`px-4 py-2 text-sm font-medium transition-colors hover:text-gold ${isContactActive ? activeLinkClass : "text-foreground"}`}
                  onMouseEnter={prefetchContactRoute}
                  onTouchStart={prefetchContactRoute}
                >
                  Contact Us
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="hidden lg:flex lg:items-center lg:gap-2">
          <Button asChild className="bg-[#0088cc] text-white font-semibold hover:bg-[#0088cc]/90">
            <a href="https://t.me/+971552622889" target="_blank" rel="noopener noreferrer">Telegram</a>
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border/40 bg-gradient-to-b from-background to-accent/5 max-h-[80vh] overflow-y-auto">
          <div className="space-y-1 px-4 py-4">
            {/* Company Name in Mobile */}
            <div className="flex flex-col items-center py-3 border-b border-border/40 mb-3">
              <span className="text-sm font-semibold text-foreground">Ghulam Mustafa Furniture</span>
              <span className="text-xs text-gold font-medium" style={{ fontFamily: 'Arial, sans-serif' }}>غلام مصطفى لتجارة الاثاث ش.ذ.م.م</span>
            </div>
            
            <Link
              to="/"
              className={`block py-2 text-base font-medium hover:text-gold ${isHomeActive ? activeLinkClass : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>

            {/* Customize Furniture Mobile */}
            <div>
              <button
                className={`flex w-full items-center justify-between py-2 text-base font-medium hover:text-gold ${isCustomFurnitureActive ? activeLinkClass : ""}`}
                onClick={() => setOpenSubmenu(openSubmenu === "furniture" ? null : "furniture")}
                onTouchStart={prefetchCustomFurnitureRoutes}
              >
                Custom Furniture Dubai
                <ChevronDown className={`h-4 w-4 transition-transform ${openSubmenu === "furniture" ? "rotate-180" : ""}`} />
              </button>
              {openSubmenu === "furniture" && (
                <div className="ml-4 space-y-1">
                  {customFurnitureItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`block py-2 text-sm hover:text-gold ${isItemActive(item.href) ? activeLinkClass : "text-muted-foreground"}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Blinds & Curtains Mobile */}
            <div>
              <button
                className={`flex w-full items-center justify-between py-2 text-base font-medium hover:text-gold ${isBlindsCurtainsActive ? activeLinkClass : ""}`}
                onClick={() => setOpenSubmenu(openSubmenu === "blinds" ? null : "blinds")}
                onTouchStart={prefetchBlindsCurtainsRoutes}
              >
                Blinds & Curtains Dubai
                <ChevronDown className={`h-4 w-4 transition-transform ${openSubmenu === "blinds" ? "rotate-180" : ""}`} />
              </button>
              {openSubmenu === "blinds" && (
                <div className="ml-4 space-y-2">
                  <Link
                    to="/blinds-curtains"
                    className={`block py-2 text-sm font-semibold hover:underline ${isItemActive("/blinds-curtains") ? activeLinkClass : "text-gold"}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    All Blinds & Curtains
                  </Link>
                  <div>
                    <h4 className="text-sm font-semibold text-gold py-1">Blinds Dubai</h4>
                    {blindItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block py-2 text-sm hover:text-gold ${isItemActive(item.href) ? activeLinkClass : "text-muted-foreground"}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold text-gold py-1">Curtains Dubai</h4>
                    {curtainItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block py-2 text-sm hover:text-gold ${isItemActive(item.href) ? activeLinkClass : "text-muted-foreground"}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Upholstery Mobile */}
            <div>
              <button
                className={`flex w-full items-center justify-between py-2 text-base font-medium hover:text-gold ${isUpholsteryActive ? activeLinkClass : ""}`}
                onClick={() => setOpenSubmenu(openSubmenu === "upholstery" ? null : "upholstery")}
                onTouchStart={prefetchUpholsteryRoutes}
              >
                Upholstery Dubai
                <ChevronDown className={`h-4 w-4 transition-transform ${openSubmenu === "upholstery" ? "rotate-180" : ""}`} />
              </button>
              {openSubmenu === "upholstery" && (
                <div className="ml-4 space-y-2">
                  <Link
                    to="/upholstery"
                    className={`block py-2 text-sm font-semibold hover:underline ${isItemActive("/upholstery") ? activeLinkClass : "text-gold"}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    All Upholstery Services
                  </Link>
                  <div>
                    <h4 className="text-sm font-semibold text-gold py-1">Indoor Upholstery</h4>
                    {indoorUpholsteryItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block py-2 text-sm hover:text-gold ${isItemActive(item.href) ? activeLinkClass : "text-muted-foreground"}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold text-gold py-1">Outdoor Upholstery</h4>
                    {outdoorUpholsteryItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`block py-2 text-sm hover:text-gold ${isItemActive(item.href) ? activeLinkClass : "text-muted-foreground"}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/projects"
              className={`block py-2 text-base font-medium hover:text-gold ${isProjectsActive ? activeLinkClass : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Projects
            </Link>

            <Link
              to="/contact"
              className={`block py-2 text-base font-medium hover:text-gold ${isContactActive ? activeLinkClass : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </Link>

            <div className="flex items-center gap-4 pt-4 border-t border-border/40">
              <Button asChild size="sm" className="bg-[#0088cc] text-white font-semibold hover:bg-[#0088cc]/90">
                <a href="https://t.me/+971552622889" target="_blank" rel="noopener noreferrer">Telegram</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
